import { Icon, IconName } from '@/components/Icon'
import { Tag } from '@/components/Tag'
import { Button } from '@/components/Button'
import { ContactLink } from '@/components/ContactLink'
import { CardPricing } from '@/components/CardPricing'
import { Tooltip } from '@/components/Tooltip'

interface PlanTag {
	label: string
	color: 'brand' | 'success' | 'content2' | 'warning' | 'danger'
	iconLeft?: IconName
}

export type PlanCardVariant = 'default' | 'no-pricing' | 'unavailable'

export interface PlanCardProps {
	planName: string
	imageUrl?: string
	beds: string
	baths: string
	sqftRange: string
	matchingUnits: number
	/** Text beside the matching unit count, e.g. "Matching Homes". */
	matchingUnitsLabel?: string
	totalPrice?: string
	baseRent?: string
	leaseTerm?: string
	tags?: PlanTag[]
	specialMessage?: string
	hasVirtualTour?: boolean
	tourUrl?: string
	onTourClick?: () => void
	hasFees?: boolean
	hasDisclaimerSymbol?: boolean
	variant?: PlanCardVariant
	/** Visually highlights the card (e.g. when the corresponding map unit is hovered). */
	highlighted?: boolean
	/** When true, hides the "Unavailable" tag on unavailable variant cards. */
	hideUnavailableTag?: boolean
	onClick?: () => void
	onHoverStart?: () => void
	onHoverEnd?: () => void
	spacesClass?: string
	className?: string
}

export function PlanCard ({
	planName,
	imageUrl,
	beds,
	baths,
	sqftRange,
	matchingUnits,
	matchingUnitsLabel = 'Matching Units',
	totalPrice,
	baseRent,
	leaseTerm,
	tags = [],
	specialMessage,
	hasVirtualTour = false,
	tourUrl,
	onTourClick,
	hasFees = true,
	hasDisclaimerSymbol = false,
	variant = 'default',
	highlighted = false,
	hideUnavailableTag = false,
	onClick,
	onHoverStart,
	onHoverEnd,
	spacesClass = '',
	className = ''
}: PlanCardProps) {
	const highlightClasses = highlighted ? 'spaces:border-brand-base spaces:shadow-sm-below' : ''

	const effectiveTags: PlanTag[] = specialMessage
		? [{ label: 'Special', color: 'success', iconLeft: 'Tag' as const }, ...tags]
		: tags

	const cardClasses = [
		'spaces-plan spaces:bg-canvas-base spaces:border spaces:border-canvas-3 spaces:rounded-16 spaces:overflow-hidden spaces:flex spaces:flex-col spaces:cursor-pointer spaces:group spaces:transition-all spaces:duration-200 spaces:hover:shadow-sm-below spaces:outline-none spaces:focus-visible:ring-2 spaces:focus-visible:ring-brand-base spaces:focus-visible:ring-offset-2',
		highlightClasses,
		highlighted && 'spaces-highlighted',
		variant === 'unavailable' && 'spaces-unavailable',
		variant === 'no-pricing' && 'spaces-no-pricing',
		spacesClass,
		className
	]
		.filter(Boolean)
		.join(' ')

	return (
		<div
			className={cardClasses}
			tabIndex={0}
			onClick={onClick}
			onKeyDown={e => {
				if ((e.key === 'Enter' || e.key === ' ') && onClick) {
					e.preventDefault()
					onClick()
				}
			}}
			onMouseEnter={onHoverStart}
			onMouseLeave={onHoverEnd}
		>
			{/* Image */}
			<div className='spaces-plan-image spaces:relative spaces:aspect-square spaces:w-full spaces:overflow-hidden spaces:bg-image-bg'>
				{imageUrl && (
					<img
						src={imageUrl}
						alt={`${planName} floor plan`}
						className='spaces:absolute spaces:inset-0 spaces:w-full spaces:h-full spaces:object-contain'
					/>
				)}
				{/* Overlay: tags + virtual tour */}
				<div className='spaces:absolute spaces:inset-0 spaces:p-12 spaces:flex spaces:items-start spaces:gap-8'>
					<div className='spaces:flex spaces:flex-1 spaces:flex-wrap spaces:gap-8 spaces:min-w-0'>
						{effectiveTags.map((tag, i) => (
							<Tag
								key={i}
								label={tag.label}
								color={tag.color}
								appearance='subtle'
								size='sm'
								iconLeft={tag.iconLeft}
							/>
						))}
					</div>
					{hasVirtualTour && (
						<Tooltip label='Virtual Tour' placement='bottom'>
							<Button
								iconOnly='Tour360'
								appearance='stroke'
								color='content4'
								size='md'
								aria-label='Virtual Tour'
								onClick={e => {
									e.stopPropagation()
									onTourClick?.()
								}}
							/>
						</Tooltip>
					)}
				</div>
			</div>

			{/* Details */}
			<div className='spaces-plan-details spaces:flex spaces:flex-col spaces:gap-12 spaces:p-16'>
				<span className='spaces:text-18/24 spaces:font-semi spaces:text-content-1'>{planName}</span>

				{/* Attributes */}
				<div className='spaces-plan-attributes spaces:overflow-hidden'>
					<div className='spaces:-ml-13 spaces:flex spaces:flex-wrap spaces:items-center spaces:gap-y-4'>
						<div className='spaces:flex spaces:items-center spaces:gap-4 spaces:border-l spaces:border-canvas-2 spaces:pl-12 spaces:pr-12'>
							<Icon name='Bed' size={24} className='spaces:text-content-4' />
							<span className='spaces:text-16/24 spaces:font-default spaces:text-content-2'>{beds}</span>
						</div>
						<div className='spaces:flex spaces:items-center spaces:gap-4 spaces:border-l spaces:border-canvas-2 spaces:pl-12 spaces:pr-12'>
							<Icon name='Bath' size={24} className='spaces:text-content-4' />
							<span className='spaces:text-16/24 spaces:font-default spaces:text-content-2'>{baths}</span>
						</div>
						{sqftRange && (
							<div className='spaces:flex spaces:items-center spaces:gap-4 spaces:border-l spaces:border-canvas-2 spaces:pl-12 spaces:pr-12'>
								<Icon name='Area' size={24} className='spaces:text-content-4' />
								<span className='spaces:text-16/24 spaces:font-default spaces:text-content-2'>
									{sqftRange} ft<sup className='spaces:text-10 spaces:font-strong'>2</sup>
								</span>
							</div>
						)}
					</div>
				</div>

				{/* Availability */}
				{variant === 'unavailable' && !hideUnavailableTag ? (
					<div className='spaces:flex spaces:items-center'>
						<Tag label='Unavailable' color='warning' appearance='subtle' size='md' />
					</div>
				) : (
					<div className='spaces:flex spaces:items-center spaces:gap-8'>
						<Tag
							label={String(matchingUnits)}
							appearance='stroke'
							color='content2'
							size='md'
						/>
						<span className='spaces-plan-matching-label spaces:text-16/24 spaces:font-default spaces:text-content-2'>
							{matchingUnitsLabel}
						</span>
					</div>
				)}
			</div>

			{/* Pricing */}
			<div
				className={`spaces-plan-pricing spaces:mt-auto spaces:flex spaces:items-center spaces:gap-8 spaces:px-16 spaces:bg-canvas-4 spaces:border-t spaces:border-canvas-3 ${
					variant !== 'default' ? 'spaces:py-24' : 'spaces:py-16'
				}`}
			>
				{variant === 'default' ? (
					<CardPricing
						hasFees={hasFees}
						totalPrice={totalPrice}
						baseRent={baseRent}
						leaseTerm={leaseTerm}
						hasDisclaimerSymbol={hasDisclaimerSymbol}
					/>
				) : (
					<ContactLink
						postText={variant === 'no-pricing' ? 'for Pricing' : 'for Information'}
					/>
				)}
			</div>
		</div>
	)
}
